<template>
  <ContentWrap>
    <el-form
      ref="queryFormRef"
      :inline="true"
      :model="queryParams"
      class="-mb-15px"
      label-width="80px"
    >
      <el-form-item label="兑换码" prop="code">
        <el-input
          v-model="queryParams.code"
          class="!w-200px"
          clearable
          placeholder="兑换码"
          @keyup.enter="handleQuery"
        />
      </el-form-item>
      <el-form-item label="批次号" prop="batchNo">
        <el-input
          v-model="queryParams.batchNo"
          class="!w-200px"
          clearable
          placeholder="批次号"
          @keyup.enter="handleQuery"
        />
      </el-form-item>
      <el-form-item label="权益" prop="rewardType">
        <el-select v-model="queryParams.rewardType" class="!w-140px" clearable placeholder="全部">
          <el-option label="优惠券" :value="1" />
          <el-option label="积分" :value="2" />
        </el-select>
      </el-form-item>
      <el-form-item label="状态" prop="status">
        <el-select v-model="queryParams.status" class="!w-140px" clearable placeholder="全部">
          <el-option label="未使用" :value="0" />
          <el-option label="已使用" :value="1" />
          <el-option label="已过期" :value="2" />
          <el-option label="已作废" :value="4" />
        </el-select>
      </el-form-item>
      <el-form-item>
        <el-button @click="handleQuery">
          <Icon class="mr-5px" icon="ep:search" />
          搜索
        </el-button>
        <el-button @click="resetQuery">
          <Icon class="mr-5px" icon="ep:refresh" />
          重置
        </el-button>
        <el-button
          v-hasPermi="['promotion:exchange-code:create']"
          plain
          type="primary"
          @click="openCreate"
        >
          <Icon class="mr-5px" icon="ep:plus" />
          批量制码
        </el-button>
        <el-button
          v-hasPermi="['promotion:exchange-code:disable']"
          :disabled="checkedIds.length === 0"
          plain
          type="danger"
          @click="handleDisableBatch"
        >
          批量作废
        </el-button>
      </el-form-item>
    </el-form>
  </ContentWrap>

  <ContentWrap>
    <el-alert
      class="mb-12px"
      type="info"
      :closable="false"
      title="一期仅支持优惠券（模板须为「指定发放」）与积分。一码一用；作废/过期不可兑。勿与 AI 卡密混淆。"
    />
    <el-table
      v-loading="loading"
      :data="list"
      :show-overflow-tooltip="true"
      :stripe="true"
      @selection-change="onSelectionChange"
    >
      <el-table-column type="selection" width="45" :selectable="rowSelectable" />
      <el-table-column align="center" label="兑换码" prop="code" min-width="160" />
      <el-table-column align="center" label="批次" prop="batchNo" min-width="140" />
      <el-table-column align="center" label="权益" width="90">
        <template #default="{ row }">
          {{ row.rewardType === 1 ? '优惠券' : row.rewardType === 2 ? '积分' : row.rewardType }}
        </template>
      </el-table-column>
      <el-table-column align="center" label="面值/模板" min-width="110">
        <template #default="{ row }">
          <span v-if="row.rewardType === 2">{{ row.rewardValue }} 积分</span>
          <span v-else>模板 #{{ row.couponTemplateId }}</span>
        </template>
      </el-table-column>
      <el-table-column align="center" label="状态" width="90">
        <template #default="{ row }">
          <el-tag v-if="row.status === 0" type="success">未使用</el-tag>
          <el-tag v-else-if="row.status === 1" type="info">已使用</el-tag>
          <el-tag v-else-if="row.status === 2" type="warning">已过期</el-tag>
          <el-tag v-else-if="row.status === 4" type="danger">已作废</el-tag>
          <span v-else>{{ row.status }}</span>
        </template>
      </el-table-column>
      <el-table-column
        :formatter="dateFormatter"
        align="center"
        label="生效"
        prop="validStartTime"
        width="170"
      />
      <el-table-column
        :formatter="dateFormatter"
        align="center"
        label="失效"
        prop="validEndTime"
        width="170"
      />
      <el-table-column align="center" label="兑用人" min-width="120" show-overflow-tooltip>
        <template #default="{ row }">
          {{ row.nickname || (row.userId ? `用户${row.userId}` : '-') }}
        </template>
      </el-table-column>
      <el-table-column
        :formatter="dateFormatter"
        align="center"
        label="兑换时间"
        prop="usedTime"
        width="170"
      />
      <el-table-column align="center" label="操作" width="100" fixed="right">
        <template #default="{ row }">
          <el-button
            v-if="row.status === 0"
            v-hasPermi="['promotion:exchange-code:disable']"
            link
            type="danger"
            @click="handleDisable(row.id)"
          >
            作废
          </el-button>
        </template>
      </el-table-column>
    </el-table>
    <Pagination
      v-model:limit="queryParams.pageSize"
      v-model:page="queryParams.pageNo"
      :total="total"
      @pagination="getList"
    />
  </ContentWrap>

  <ExchangeCodeCreateForm ref="createFormRef" @success="getList" />
</template>

<script lang="ts" setup>
import { dateFormatter } from '@/utils/formatTime'
import { ExchangeCodeApi } from '@/api/mall/promotion/exchangeCode'
import ExchangeCodeCreateForm from './ExchangeCodeCreateForm.vue'

defineOptions({ name: 'PromotionExchangeCode' })

const message = useMessage()

const loading = ref(true)
const total = ref(0)
const list = ref([])
const checkedIds = ref<number[]>([])
const queryParams = reactive({
  pageNo: 1,
  pageSize: 20,
  code: undefined as string | undefined,
  batchNo: undefined as string | undefined,
  rewardType: undefined as number | undefined,
  status: undefined as number | undefined
})
const queryFormRef = ref()
const createFormRef = ref()

const rowSelectable = (row: any) => row.status === 0

const onSelectionChange = (rows: any[]) => {
  checkedIds.value = rows.map((r) => r.id)
}

const getList = async () => {
  loading.value = true
  try {
    const data = await ExchangeCodeApi.getPage(queryParams)
    list.value = data.list
    total.value = data.total
  } finally {
    loading.value = false
  }
}

const handleQuery = () => {
  queryParams.pageNo = 1
  getList()
}

const resetQuery = () => {
  queryFormRef.value?.resetFields()
  handleQuery()
}

const openCreate = () => createFormRef.value?.open()

const handleDisable = async (id: number) => {
  try {
    await message.confirm('确认作废该兑换码？作废后不可再兑')
    await ExchangeCodeApi.disable(id)
    message.success('已作废')
    await getList()
  } catch {}
}

const handleDisableBatch = async () => {
  try {
    await message.confirm(`确认作废选中的 ${checkedIds.value.length} 个兑换码？`)
    await ExchangeCodeApi.disableList(checkedIds.value)
    message.success('已作废')
    await getList()
  } catch {}
}

onMounted(() => getList())
</script>
